import type { QueryResult } from '@apollo/client';
import type { ListVariantTypeAndProps } from '@aurora/shared-client/components/common/List';
import { ListItemSpacing, ListVariant } from '@aurora/shared-client/components/common/List/enums';
import List from '@aurora/shared-client/components/common/List/List';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import QueryHandler from '@aurora/shared-client/components/common/QueryHandler/QueryHandler';
import { LoadingSize } from '@aurora/shared-client/types/enums';
import React, { useContext } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { getItems } from '../../../../helpers/list/EntityListHelper';
import type { ItemType } from '../../../../types/enums';
import EditContext from '../../../context/EditContext/EditContext';
import type { ItemViewTypeAndProps, ItemViewVariant } from '../../../entities/types';
import ItemView from '../../ItemView/ItemView';
import localStyles from './PaneledItemList.module.pcss';

/**
 * Props for `PaneledItemListItems`.
 */
interface Props<
  Item,
  TypeT extends ItemType,
  ViewVariantT extends ItemViewTypeAndProps<TypeT, ItemViewVariant<TypeT>>,
  TData,
  TVariables
> {
  /**
   * The query result from a GraphQL call.
   */
  queryResult: QueryResult<TData, TVariables>;
  /**
   * The size of the loading indicator.
   */
  loadingSize?: LoadingSize;
  /**
   * The variant display style used when rendering the message list.
   */
  listVariant?: ListVariantTypeAndProps<Item>;
  /**
   * The path to the items in the query results. This is used to get the items from
   * the query result for display and used for updating the items during pagination.
   * If left unspecified then the item path is assumed to simply be the key of the first
   * property inside the `data` object.
   */
  itemPath?: string;
  /**
   * A component to display when the list is empty.
   */
  empty?: React.FC<React.PropsWithChildren<unknown>>;
  /**
   * Applies panel styling to the component.
   */
  panel?: PanelType;
  /**
   * The path to the sub object in the query result, from where the items should
   * be taken. Used when the items to display are not part of the parent Node object.
   */
  querySubObject?: string;
  /**
   * Whether to display empty state or not.
   * When set to true, empty state is displayed only when there is no content to show.
   * If set to false, empty state is never displayed.
   */
  useEmpty?: boolean;
  /**
   * View variant type and props for the entity of type EntityT.
   */
  variant: ViewVariantT;
  /**
   * Item type for entity of type EntityT.
   */
  type: TypeT;
}

/**
 * Creates a `List` from a `QueryResult`. The list is rendered based on the specified panel type.
 * The general markup generated from this list:
 * <code>
 *     <section class="lia-panel-list-body">
 *       <ul>
 *          <li><ItemView of EntityT</li>
 *          <li><ItemView of EntityT</li>
 *          <li><ItemView of EntityT</li>
 *          <li><ItemView of EntityT</li>
 *          <li><ItemView of EntityT</li>
 *          <li><ItemView of EntityT</li>
 *        </ul>
 *     </section>
 * </code>
 * @author rishabh.jivan
 */
const PaneledItemListItems = <
  EntityT,
  TypeT extends ItemType,
  ViewVariantT extends ItemViewTypeAndProps<TypeT, ItemViewVariant<TypeT>>,
  TData,
  TVariables
>({
  queryResult,
  loadingSize = LoadingSize.LG,
  listVariant,
  itemPath,
  empty: Empty,
  panel = PanelType.STANDARD,
  querySubObject,
  variant,
  type,
  useEmpty = false
}: Props<EntityT, TypeT, ViewVariantT, TData, TVariables>): React.ReactElement => {
  const cx = useClassNameMapper(localStyles);

  const panelTypeToListGroupMap: Record<PanelType, ListVariantTypeAndProps<EntityT>> = {
    [PanelType.STANDARD]: {
      type: ListVariant.UNSTYLED
    },
    [PanelType.DIVIDER]: {
      type: ListVariant.LIST_GROUP
    },
    [PanelType.BUBBLE]: {
      type: ListVariant.UNSTYLED,
      props: {
        listItemSpacing: ListItemSpacing.MD
      }
    },
    [PanelType.SPACED]: {
      type: ListVariant.UNSTYLED,
      props: {
        listItemSpacing: ListItemSpacing.MD
      }
    },
    [PanelType.NONE]: { type: ListVariant.UNSTYLED }
  };

  /**
   * renders the entity.
   * @param entity the entity to render.
   */
  function renderItem(entity: EntityT): React.ReactElement {
    return <ItemView<EntityT, TypeT, ViewVariantT> entity={entity} variant={variant} type={type} />;
  }

  const { showEditControls } = useContext(EditContext);

  return (
    <QueryHandler<TData, TVariables> queryResult={queryResult} loadingSize={loadingSize}>
      {(): React.ReactNode => {
        const items: EntityT[] = getItems(queryResult, itemPath, querySubObject);
        const hasItems = items?.length > 0;
        const useEmptyState = Empty != undefined && !hasItems && (useEmpty || showEditControls);

        const listVariantOrDefault: ListVariantTypeAndProps<EntityT> =
          listVariant ?? panelTypeToListGroupMap[panel];
        const decoratedListVariant = {
          ...listVariantOrDefault,
          props: {
            ...listVariantOrDefault?.props,
            listItemClassName: (item: EntityT): string =>
              cx('lia-panel-list-item', listVariantOrDefault?.props?.listItemClassName?.(item))
          }
        };
        return (
          <>
            {!useEmptyState && (
              <List<EntityT>
                items={items}
                variant={decoratedListVariant as ListVariantTypeAndProps<EntityT>}
              >
                {renderItem}
              </List>
            )}
            {useEmptyState && <Empty />}
          </>
        );
      }}
    </QueryHandler>
  );
};

export default PaneledItemListItems;
